import PeopleAltTwoToneIcon from '@mui/icons-material/PeopleAltTwoTone';
import { darken, Stack, Typography } from '@mui/material';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { OutreachTypes, SearchObjective } from '../../campaign-builder.types';
import { useTranslation } from 'react-i18next';

interface Props {
  selected: boolean;
  objective: SearchObjective;

  handleSelectContactSearchObjective(newObjective: SearchObjective): () => void;
}

const ObjectiveCard: React.FC<Props> = ({ selected, objective, handleSelectContactSearchObjective }) => {
  const { outreachType, title, subTitle, whenToUseText, howToUseText, exampleUrl } = objective;

  const { classes, cx } = useStyles({ outreachType });
  const { t } = useTranslation();

  return (
    <Stack className={cx(classes.objectiveCard, selected ? 'checked' : undefined)} onClick={handleSelectContactSearchObjective(objective)}>
      <div className={classes.cardHead}>
        <Stack direction={'row'} gap={2}>
          <PeopleAltTwoToneIcon className={classes.icon} />
          <Typography sx={{ lineHeight: 1.2 }}>
            <span className={classes.title}>{title}:</span>
            <span className={classes.subTitle}> {subTitle}</span>
          </Typography>
        </Stack>
        <div className={cx(classes.checkbox, selected ? 'checked' : undefined)} />
      </div>
      <Stack className={classes.cardBody} gap={2}>
        <Typography sx={{ lineHeight: 1.2 }}>
          <span className={classes.underlineCardText}>{t('whenToUse')}:</span>
          <span className={classes.cardText}> {whenToUseText}</span>
        </Typography>
        <Typography sx={{ lineHeight: 1.2 }}>
          <span className={classes.underlineCardText}>{t('howToUse')}:</span>
          <span className={classes.cardText}> {howToUseText}</span>
        </Typography>
      </Stack>
      {selected ? (
        <div className={classes.cardFooter}>
          <PaiLink text={t('useThisLink')} size={'md'} variant={'primary'} href={exampleUrl} target={'_blank'} />
        </div>
      ) : null}
    </Stack>
  );
};

const useStyles = makeStyles<{ outreachType: OutreachTypes }>()((theme, { outreachType }) => ({
  objectiveCard: {
    cursor: 'pointer',
    background: '#FFF',
    borderRadius: '6px',
    width: '100%',
    border: `1px solid ${theme.palette.greys.light}`,
    '&:hover': {
      background: darken('#FFF', 0.05),
    },
    '&.checked': {
      border: `1px solid ${outreachType === OutreachTypes.COLD ? theme.palette.primary.main : theme.palette.secondary.main}`,
    },
  },
  cardHead: {
    position: 'relative',
    padding: '16px',
    paddingRight: 'calc(16px + 20px + 16px)',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  icon: {
    fontSize: theme.typography.pxToRem(36),
    color: outreachType === OutreachTypes.COLD ? theme.palette.primary.main : theme.palette.secondary.main,
  },
  title: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    color: outreachType === OutreachTypes.COLD ? theme.palette.primary.main : theme.palette.secondary.main,
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  checkbox: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '20px !important',
    height: '20px !important',
    borderRadius: '4px',
    border: `2px solid ${theme.palette.greys.light}`,
    '&.checked': {
      border: `2px solid ${outreachType === OutreachTypes.COLD ? theme.palette.primary.main : theme.palette.secondary.main}`,
      background: outreachType === OutreachTypes.COLD ? theme.palette.primary.main : theme.palette.secondary.main,
    },
  },
  cardBody: {
    padding: '16px',
  },
  underlineCardText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    textDecoration: 'underline',
  },
  cardText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  cardFooter: {
    padding: '16px',
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
}));

export default ObjectiveCard;
